import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { auth } from '../lib/firebase';

/**
 * InstallHint — small dismissable banner that nudges the user to install
 * listicity as a PWA. Two modes, picked automatically:
 *
 *   1. iOS Safari (and iOS Chrome — both require PWA-install for push):
 *      "tap Share → Add to Home Screen" copy with a manual instruction
 *      pointer. iOS has no installation API so we just teach the gesture.
 *
 *   2. Chrome / Edge desktop, Android Chrome, etc.: shown only when the
 *      browser has fired `beforeinstallprompt` (captured by
 *      useInstallPrompt at module scope). Renders an "install" button
 *      that calls the deferred event when clicked.
 *
 * Position: top-center (Walter 2026-05-15) so the banner doesn't compete
 * with bottom-anchored UI like the agenda toggle or undo toast.
 *
 * Dismissal: clicking × persists a localStorage flag — the auto-banner
 * stops appearing for good. The user can still trigger an install from
 * Settings → Install app (which reads the same useInstallPrompt
 * singleton). On dismiss we briefly morph the banner into a confirmation
 * note pointing at Settings so the user knows where to find it.
 */

const DISMISS_KEY = 'lists_install_hint_dismissed';

// Walter 2026-06-01: floating install POP-UP disabled for F&F. After
// sign-in it overlapped the Getting Started checklist's install step —
// two prompts for the same thing read as nagging. Install stays
// discoverable via the checklist (2/2) + Settings → Install app; we just
// don't auto-pop a banner. PARKED on the backlog: decide whether to
// reintroduce it later (e.g. only after N days / sustained usage) or
// drop it entirely. Flip this flag to re-enable (the component is intact
// below). All hooks still run when off so Rules-of-Hooks hold; the
// `mode` gate just resolves to null.
const SHOW_INSTALL_HINT = false;

export default function InstallHint() {
  const { canInstall, isIOS, isStandalone, prompt } = useInstallPrompt();

  // Persistent dismissal flag. Read once on mount.
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  // After the user clicks ×, we show a brief "find it in Settings" note
  // before fully hiding. Auto-clears after 6s.
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);

  useEffect(() => {
    if (!confirmingDismiss) return undefined;
    const t = setTimeout(() => setConfirmingDismiss(false), 6000);
    return () => clearTimeout(t);
  }, [confirmingDismiss]);

  // Walter 2026-05-31: don't prompt install until the user is signed in.
  // On mobile this banner is the ONLY install nudge (the sidebar
  // onboarding checklist is desktop-only), and it was firing for
  // anonymous guests the moment the browser deemed the PWA installable —
  // before they'd even signed in. Installing as a guest also means their
  // data isn't saved to an account, so sign-in genuinely should come
  // first. We track a minimal auth subscription here because InstallHint
  // renders above the app's auth context. Default false → banner stays
  // hidden until we positively know the user is signed in (non-anonymous).
  const [signedIn, setSignedIn] = useState(
    () => !!(auth.currentUser && !auth.currentUser.isAnonymous),
  );
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setSignedIn(!!(u && !u.isAnonymous));
    });
  }, []);

  // Walter 2026-05-16: suppress the banner on marketing pages. The
  // top:64px position is calibrated to clear the app's ~48px header,
  // but the marketing pages have a slimmer header and use the top of
  // the viewport for hero copy ("A canvas for your lists."). The
  // banner was landing on top of that text on the landing page — bad
  // first impression for new visitors. The marketing pages already
  // have their own "Start using listicity →" CTA in the header, so
  // the install banner is redundant there anyway. Banner reappears
  // automatically once the user enters the app (board / list / etc.).
  const location = useLocation();
  const onMarketingRoute = /^\/(landing|about|help|privacy|terms|contact)\b/.test(
    location.pathname,
  ) || location.pathname === '/';

  // Decide which banner variant to show (or none).
  // - On marketing routes → never show (suppress overlap with hero)
  // - Already installed (standalone mode) → never show
  // - Permanently dismissed (and not in the confirm-dismiss step) → none
  // - iOS device → 'ios' variant
  // - Chrome/Edge/Android with deferred prompt available → 'prompt' variant
  // - Otherwise (Firefox, Safari desktop without standalone) → none
  let mode = null;
  // `signedIn` gate: never prompt install before the user has an account
  // (Walter 2026-05-31). The confirm-dismiss note is allowed through
  // regardless so a mid-dismiss state isn't left dangling.
  if (SHOW_INSTALL_HINT && !isStandalone && !onMarketingRoute && (signedIn || confirmingDismiss)) {
    if (confirmingDismiss) {
      mode = 'confirm';
    } else if (!dismissed) {
      if (isIOS) mode = 'ios';
      else if (canInstall) mode = 'prompt';
    }
  }

  if (!mode) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
    setConfirmingDismiss(true);
  };

  const handleInstallClick = async () => {
    const choice = await prompt();
    // Persist dismissal regardless of outcome — the deferred event is
    // single-use and won't fire again this page load. If the user said
    // no, treat as dismissal so we don't re-nag.
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
    if (choice?.outcome !== 'accepted') {
      // Show the "find it in Settings" hint so they know how to
      // get back to it.
      setConfirmingDismiss(true);
    }
  };

  const containerStyle = {
    position: 'fixed',
    // Walter 2026-05-15: top-center instead of bottom-center so it
    // doesn't compete with the agenda toggle / undo toast / mobile
    // bottom chrome. Offset clears the header on both desktop + mobile.
    left: '50%',
    top: '64px',
    transform: 'translateX(-50%)',
    zIndex: 2400,
    maxWidth: 'min(420px, calc(100vw - 24px))',
    background: 'var(--surface)',
    border: '1px solid var(--accent-dim)',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-toast-mid)',
    padding: '12px 14px',
    fontFamily: 'var(--mono)',
    fontSize: 'calc(11px * var(--ui-scale, 1))',
    color: 'var(--text)',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    animation: 'lists-fadein 0.2s ease',
  };

  const xButton = (onClick) => (
    <button
      onClick={onClick}
      title="dismiss"
      aria-label="dismiss"
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text-dimmer)',
        padding: '4px',
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </button>
  );

  if (mode === 'confirm') {
    return (
      <div style={containerStyle}>
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.55, color: 'var(--text-dim)' }}>
          You can install listicity anytime from{' '}
          <strong style={{ color: 'var(--text)' }}>Settings → Install app</strong>.
        </div>
        {xButton(() => setConfirmingDismiss(false))}
      </div>
    );
  }

  if (mode === 'ios') {
    return (
      <div style={containerStyle}>
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--accent)' }}>Install listicity on your iPhone</strong>
          <div style={{ marginTop: '4px', color: 'var(--text-dim)' }}>
            Get push reminders + a home-screen icon. Tap the{' '}
            <strong>Share</strong> button{' '}
            <span style={{ color: 'var(--accent)' }}>↑</span> in Safari, then{' '}
            <strong>Add to Home Screen</strong>. Open listicity from the
            home-screen icon and enable push there.
          </div>
        </div>
        {xButton(dismiss)}
      </div>
    );
  }

  // mode === 'prompt' — Chrome / Edge / Android Chrome
  return (
    <div style={containerStyle}>
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.55 }}>
        <strong style={{ color: 'var(--accent)' }}>Install listicity</strong>
        <div style={{ marginTop: '4px', color: 'var(--text-dim)' }}>
          Quicker launch from your dock, offline access, and push
          reminders. One click — same app, no app store.
        </div>
        <button
          onClick={handleInstallClick}
          style={{
            marginTop: '8px',
            padding: '6px 12px',
            border: '1px solid var(--accent)',
            background: 'var(--accent)',
            color: 'var(--bg)',
            borderRadius: 'var(--radius-sm, 6px)',
            cursor: 'pointer',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            fontWeight: 600,
          }}
        >
          install
        </button>
      </div>
      {xButton(dismiss)}
    </div>
  );
}
